import { useImageAnnotatorContext } from "@msuite/image-annotator";
import type { TranslationKey } from "@msuite/utils/locales";
import { useMemo, useState } from "react";
import { Icon } from "../../../icons";
import { useTranslation } from "../../../localization";
import { Tooltip } from "../../../tooltip";
import { cn } from "../../../utils";
import { AnnotationToolbarSubMenu } from "./annotation-toolbar-sub-menu";
import type { AnnotationToolbarUpdateTarget } from "./shared";

interface OptionItem {
	target: AnnotationToolbarUpdateTarget;
	label: TranslationKey;
	iconName: Parameters<typeof Icon>[0]["name"];
}

const OPTIONS_BY_TYPE: Record<"polygon" | "dimension" | "text", Array<OptionItem>> = {
	polygon: [
		{ target: "stroke", label: "image_annotator.stroke", iconName: "SquareDashed" },
		{ target: "fill", label: "image_annotator.fill", iconName: "Square" },
		{ target: "pattern", label: "image_annotator.pattern", iconName: "Grid3x3" },
		{ target: "text", label: "image_annotator.text", iconName: "Type" },
	],
	dimension: [
		{ target: "stroke", label: "image_annotator.stroke", iconName: "SquareDashed" },
		{ target: "offset", label: "image_annotator.offset", iconName: "ArrowDownWideNarrow" },
		{ target: "tilt", label: "image_annotator.tilt", iconName: "TriangleRight" },
		{ target: "text", label: "image_annotator.text", iconName: "Type" },
		{ target: "unit", label: "units.unit", iconName: "RulerDimensionLine" },
	],
	text: [
		{ target: "stroke", label: "image_annotator.stroke", iconName: "SquareDashed" },
		{ target: "text", label: "image_annotator.text", iconName: "Type" },
		{ target: "font-size", label: "image_annotator.font_size", iconName: "CaseSensitive" },
	],
};

export function AnnotationToolbarOptions() {
	/** Context */
	const context = useImageAnnotatorContext();
	const selectedAnnotation = context.controller.getSelectedAnnotation();

	/** State */
	const [selectedOption, setSelectedOption] = useState<AnnotationToolbarUpdateTarget | null>(null);

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const items = useMemo(() => {
		const type = selectedAnnotation?.type;
		if (type === "polygon" || type === "dimension" || type === "text") {
			return OPTIONS_BY_TYPE[type];
		}
		return [];
	}, [selectedAnnotation]);

	/** Functions */
	function handleOnOpenOption(target: AnnotationToolbarUpdateTarget) {
		setSelectedOption((prev) => (prev === target ? null : target));
	}

	/** Guard */
	if (!selectedAnnotation) return null;

	/** Render */
	return (
		<div className="relative">
			<div className="flex flex-row items-center gap-2 overflow-x-auto">
				{items.map((item) => (
					<Tooltip
						key={item.target}
						label={t(item.label)}
						placement="top"
						zIndexClassName="z-[999999]"
					>
						<button
							type="button"
							onClick={() => handleOnOpenOption(item.target)}
							className={cn(
								"flex-center h-9 w-9 shrink-0 rounded-full text-white",
								selectedOption === item.target ? "bg-primary-700" : "bg-primary-800",
							)}
						>
							<Icon
								name={item.iconName}
								size={16}
							/>
						</button>
					</Tooltip>
				))}
				<Tooltip
					label={t("general.delete")}
					placement="top"
					zIndexClassName="z-[999999]"
				>
					<button
						type="button"
						onClick={() => context.controller.handleOnDeleteSelection()}
						className="flex-center h-9 w-9 shrink-0 rounded-full bg-primary-800 text-red-500"
					>
						<Icon
							name="Trash2"
							size={16}
						/>
					</button>
				</Tooltip>
			</div>
			{selectedOption && <AnnotationToolbarSubMenu target={selectedOption} />}
		</div>
	);
}
